import { useEffect, useState } from "react";
import type { LoaderFunction } from "react-router-dom";
import { Link, useLoaderData } from "react-router-dom";
import { FallbackImage } from "../components/FallbackImage";
import { CalendarIcon, HeartIcon, missingArtistFallbackIcon } from "../components/icons";
import { LinkifiedDescription } from "../components/LinkifiedDescriptions";
import { PlayerWidget } from "../components/PlayerWidget";
import { FavoriteHeart } from "../components/ui/FavoriteHeart";
import { addToGoogleCalendar } from "../lib/addToGcal";
import { formatDate, formatTime2Digit } from "../lib/formatTime";
import {
  getArtistById,
  getDayById,
  getPerformanceById,
  getStageById,
  loadFestivalData,
  getStageColor,
} from "../services/dataService";
import { isFavorite, toggleFavorite } from "../services/favoritesService";
import type { Artist, Day, Performance, Stage } from "../types";
import { BasePage } from "./BasePage";

type PerformanceWithDetails = Performance & {
  artist: Artist | undefined;
  day: Day | undefined;
  stage: Stage | undefined;
};

export const loadPerformancePageData: LoaderFunction<PerformanceWithDetails> = async ({
  params,
}) => {
  await loadFestivalData();
  const performanceId = params.id;
  if (!performanceId) {
    throw new Response("Performance ID is required", { status: 400 });
  }

  const performance = getPerformanceById(performanceId);
  if (!performance) {
    throw new Response("Performance not found", { status: 404 });
  }

  return {
    ...performance,
    artist: getArtistById(performance.artistId),
    day: getDayById(performance.dayId),
    stage: getStageById(performance.stageId),
  };
};

const getHighlights = (seed: string, offset: number) => {
  let hash = 0;
  const s = seed + offset.toString();
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  const val = Math.abs(hash);
  const beforeIdx = (val % 3) + 1;
  const afterIdx = (Math.floor(val / 3) % 3) + 1;
  return `ellipse-highlight-before-${beforeIdx} ellipse-highlight-after-${afterIdx}`;
};

export const PerformancePage: React.FC = () => {
  const performance = useLoaderData() as PerformanceWithDetails;
  const [isPerformanceFavorite, setIsPerformanceFavorite] = useState(false);

  useEffect(() => {
    setIsPerformanceFavorite(isFavorite(performance.id));
  }, [performance.id]);

  const handleFavoriteClick = () => {
    toggleFavorite(performance.id);
    setIsPerformanceFavorite(!isPerformanceFavorite);
  };

  if (!performance.artist || !performance.day || !performance.stage) {
    return (
      <BasePage>
        <div className="text-center py-12 bg-card border border-border rounded-2xl">
          <h2 className="text-xl font-bold text-white">
            Az esemény nem található - valami hiba történt <br />
            <span className="text-xs text-muted font-mono">{performance.id}</span>
          </h2>
        </div>
      </BasePage>
    );
  }

  const tagline = [performance.artist.qualifier, performance.artist.collective]
    .filter(Boolean)
    .join(" / ");

  const stageColor = getStageColor(performance.stageId);
  const performanceImage = performance.image || performance.artist?.image;

  return (
    <BasePage>
      <div className="space-y-6 pb-6">
        {/* Performance Header Banner */}
        <div className="relative rounded-2xl overflow-hidden border border-white/5 bg-[#222222]/65">
          <div
            className="min-h-[192px] bg-black/40 flex items-end justify-start relative z-10 animate-bg-scroll py-6 px-5"
            style={{
              backgroundImage: performanceImage ? `url(${performanceImage})` : "none",
              backgroundSize: "cover",
            }}
          >
            {/* If no image, show the fallback image leveraging the playerUrl */}
            {!performanceImage && (
              <FallbackImage
                playerUrl={performance.artist?.playerUrl}
                variant="banner"
                alt={performance.artist?.name}
              />
            )}

            {/* Dark Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent z-0" />

            <div className="relative z-10 w-full">
              <h2 className="text-2xl font-bold font-['Unbounded'] text-white uppercase tracking-tight mt-1 drop-shadow-md">
                {performance.artist?.name || "Ismeretlen előadó"}
                {performance.artist?.collective && (
                  <span className="block text-xs font-semibold text-muted mt-1 normal-case font-sans tracking-normal">
                    {performance.artist.collective}
                  </span>
                )}
              </h2>

              <div className="flex justify-between items-center mt-4 px-1">
                {performance.day && (
                  <span
                    className={`bg-black/35 text-accent px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm text-center inline-flex items-center justify-center ${getHighlights(performance.id, 1)}`}
                  >
                    {performance.day.shortname}
                  </span>
                )}
                <span
                  className={`bg-black/35 text-main px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm text-center inline-flex items-center justify-center ${getHighlights(performance.id, 2)}`}
                >
                  {formatTime2Digit(performance.startTime)} -{" "}
                  {formatTime2Digit(performance.endTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content list */}
        <div className="space-y-6">
          {/* Big Prominent Block Like Button */}
          <FavoriteHeart
            id={`favorite-btn-${performance.id}`}
            active={isPerformanceFavorite}
            onClick={handleFavoriteClick}
            className={`w-full text-left px-5 py-3.5 rounded-2xl border block select-none focus:outline-none ${
              isPerformanceFavorite
                ? "bg-[#F7C4FF]/15 border-[#F7C4FF]/45 text-white shadow-[0_0_15px_rgba(247,196,255,0.12)]"
                : "bg-[#222222]/65 border-white/5 text-white hover:bg-white/5 hover:border-white/10"
            }`}
            targetName={performance.artist.name}
          >
            {/* Row 1: Heart Icon Centered */}
            <div className="flex items-center justify-center mb-1">
              <HeartIcon
                filled={isPerformanceFavorite}
                className={`h-6 w-6 transition-transform duration-300 ${
                  isPerformanceFavorite ? "text-[#F7C4FF] scale-110" : "text-muted opacity-70"
                }`}
              />
            </div>

            {/* Row 2: Artist/Performance Name Centered */}
            <div className="text-center mb-1.5">
              <div className="font-['Unbounded'] text-lg font-bold text-white uppercase tracking-tight">
                {performance.artist.name}
              </div>
              {tagline && <p className="text-[11px] text-muted italic mt-0.5">({tagline})</p>}
            </div>

            {/* Row 3: Prominent Time Slot */}
            <div className="text-center font-mono text-sm font-bold text-accent mb-2.5">
              {formatTime2Digit(performance.startTime)} - {formatTime2Digit(performance.endTime)}
            </div>

            {/* Row 4: Date & Stage Aligned Left & Right */}
            <div className="flex items-center justify-between text-xs border-t border-white/5 pt-2.5">
              <span className="text-muted font-medium">{formatDate(performance.day.date)}</span>
              <span style={{ color: stageColor }} className="font-bold flex items-center gap-1.5">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: stageColor }}
                />
                {performance.stage.name}
              </span>
            </div>
          </FavoriteHeart>

          {/* Google Calendar action */}
          <div className="bg-[#222222]/65 border border-white/5 p-4 rounded-2xl backdrop-blur-md">
            <button
              type="button"
              role="button"
              onClick={() =>
                addToGoogleCalendar({
                  startTime: performance.startTime,
                  endTime: performance.endTime,
                  dayId: performance.dayId,
                  artist: {
                    name: performance.artist?.name || "",
                    description: performance.artist?.description || "",
                  },
                  stage: {
                    name: performance.stage?.name || "",
                  },
                })
              }
              className="w-full py-2.5 px-4 bg-main/15 border border-main/35 text-main rounded-xl flex items-center justify-center gap-2 hover:bg-main/25 transition-all font-semibold text-sm"
              aria-label="Hozzáadás a Google Naptárhoz"
            >
              <CalendarIcon className="h-4.5 w-4.5" />
              <span>Hozzáadás a naptárhoz</span>
            </button>
          </div>

          {/* Description */}
          {performance.description && performance.description.trim() !== "" && (
            <div className="bg-[#222222]/65 border border-white/5 rounded-2xl p-5 backdrop-blur-md">
              <h3 className="font-['Unbounded'] text-sm font-bold text-main mb-2.5 uppercase tracking-wider">
                Leírás
              </h3>
              <LinkifiedDescription
                description={performance.description}
                className="text-sm text-muted leading-relaxed whitespace-pre-line"
              />
            </div>
          )}

          {/* Player Embed */}
          {performance.artist.playerUrl && (
            <div className="bg-[#222222]/65 border border-white/5 p-5 rounded-2xl backdrop-blur-md">
              <h3 className="font-['Unbounded'] text-sm font-bold text-main mb-3.5 uppercase tracking-wider">
                <span className="ellipse-highlight-before-2 ellipse-highlight-after-3">
                  Zene / Mix
                </span>
              </h3>
              <PlayerWidget url={performance.artist.playerUrl} />
            </div>
          )}

          {/* Artist Card Link */}
          <Link
            to={`/artist/${performance.artistId}`}
            className="block group"
            aria-label={`Előadó profilja: ${performance.artist.name}`}
          >
            <div className="bg-[#222222]/40 border border-white/5 p-5 rounded-2xl transition-all group-hover:border-main/40 group-hover:bg-[#333333]/50">
              <h3 className="font-['Unbounded'] text-sm font-bold text-white mb-3 uppercase tracking-wider group-hover:text-main transition-colors">
                Előadó Profilja
              </h3>
              <div className="flex items-center space-x-3.5">
                <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-full flex-shrink-0 overflow-hidden">
                  {performance.artist.image ? (
                    <img
                      src={performance.artist.image}
                      alt={performance.artist.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = missingArtistFallbackIcon;
                      }}
                    />
                  ) : (
                    <FallbackImage
                      playerUrl={performance.artist.playerUrl}
                      variant="avatar"
                      alt={performance.artist.name}
                    />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-white text-sm group-hover:underline">
                    {performance.artist.name}
                  </p>
                  {tagline && <p className="text-[11px] text-muted italic mt-0.5">({tagline})</p>}
                  {performance.artist.genre && (
                    <p className="text-xs text-muted mt-0.5">{performance.artist.genre}</p>
                  )}
                </div>
              </div>
              {performance.artist.description && (
                <p className="mt-3.5 text-xs text-muted line-clamp-2 leading-relaxed">
                  {performance.artist.description}
                </p>
              )}
            </div>
          </Link>

          {/* Stage link */}
          {performance.stage && (
            <div className="bg-[#222222]/40 border border-white/5 p-5 rounded-2xl transition-all hover:bg-[#333333]/50">
              <Link
                to={`/stage/${performance.stageId}`}
                className="block group"
                aria-label={`Színpad programja: ${performance.stage.name}`}
              >
                <h4 className="font-['Unbounded'] font-bold text-sm text-white group-hover:text-main transition-colors uppercase tracking-wide flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stageColor }} />
                  {performance.stage.name}
                </h4>
                {performance.stage.description && (
                  <p className="text-xs text-muted mt-1.5 leading-relaxed">
                    {performance.stage.description}
                  </p>
                )}
              </Link>
            </div>
          )}
        </div>
      </div>
    </BasePage>
  );
};

export default PerformancePage;
